import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ShieldCheck, Lock, HardHat, AlertTriangle } from 'lucide-react'
import { useCompliance } from '@/hooks/use-compliance'
import { useClients, clientName } from '@/hooks/use-clients'
import { useSwmsDebt, useSafetyOverrides } from '@/hooks/use-swms'
import { useCompany, useTeam } from '@/hooks/use-company'
import { hrcwLabel, jurisdictionFor } from '@/lib/hrcw'
import { phFormat } from '@/lib/ph'
import { FEATURES } from '@/lib/features'
import {
  Page, PageHeader, Card, Table, Trow, SkeletonRows, EmptyState,
  Pagination, TabPills, SearchInput, btnSecondary,
} from '@/components/rm'

// Matches the invoices/jobs list idiom (spec 006) rather than inventing a new one.
const COLS = '130px 1.4fr 1fr 120px 100px'

export const Route = createFileRoute('/_app/compliance')({ component: CompliancePage })

const fmtDate = (iso: string) =>
  phFormat(new Date(iso), 'date', { day: 'numeric', month: 'short', year: 'numeric' })

function CompliancePage() {
  const navigate = useNavigate()
  const [docType, setDocType] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [section, setSection] = useState<'docs' | 'safety'>('docs')
  const { company } = useCompany()
  // The Safety tab appears only for companies that have declared high-risk work. A compliance
  // register with a permanently empty safety tab teaches people to ignore the tab.
  const safetyOn = FEATURES.swms && (company?.hrcw_categories ?? []).length > 0

  const { docs, total, pageSize, isLoading, isError, error, refetch, docTypes } =
    useCompliance({ docType, search, page })
  const { clients } = useClients()

  const nameFor = (cid: string | null) => {
    if (!cid) return '—'
    const c = clients.find((x) => x.id === cid)
    return c ? clientName(c) : '—'
  }

  return (
    <Page>
      <PageHeader
        title="Compliance"
        subtitle={`${total} issued document${total === 1 ? '' : 's'}`}
      />

      {safetyOn && (
        <div className="mt-5">
          <TabPills
            chips={[{ id: 'docs', label: 'Documents' }, { id: 'safety', label: 'Safety' }]}
            active={section}
            onChange={(id) => setSection(id as 'docs' | 'safety')}
          />
        </div>
      )}

      {section === 'safety' ? <SafetySection /> : <>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <TabPills
          chips={[
            { id: '', label: 'All', count: total },
            ...docTypes.map((t) => ({ id: t, label: t })),
          ]}
          active={docType}
          onChange={(id) => { setDocType(id); setPage(0) }}
        />
        <div className="w-full sm:w-64">
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(0) }}
            placeholder="Search certificate number"
          />
        </div>
      </div>

      <div className="mt-5">
        <Card>
          {isLoading ? (
            <SkeletonRows count={5} />
          ) : isError ? (
            // Distinct from "no documents" — an empty compliance register when the fetch failed
            // would be a very bad thing to show someone answering an insurer.
            <div className="px-5 py-10 text-center">
              <p className="text-[14px] font-semibold text-token-primary">Couldn’t load your documents</p>
              <p className="mt-1 text-[13px] text-token-secondary">{error?.message ?? 'Please try again.'}</p>
              <button className={btnSecondary + ' mt-4'} onClick={() => refetch()}>Try again</button>
            </div>
          ) : docs.length === 0 ? (
            <EmptyState
              label={
                search || docType
                  ? 'No documents match that filter.'
                  : 'No compliance documents issued yet. Add a certificate template in Forms, then issue one from a job.'
              }
              cta={search || docType ? undefined : 'Go to Forms'}
              onCta={search || docType ? undefined : () => navigate({ to: '/forms' })}
            />
          ) : (
            <Table
              cols={COLS}
              header={<><span>Number</span><span>Type</span><span>Client</span><span>Issued</span><span className="text-right">Status</span></>}
            >
              {docs.map((d, idx) => (
                <Trow
                  key={d.id}
                  cols={COLS}
                  last={idx === docs.length - 1}
                  onClick={() => navigate({ to: '/compliance/$id', params: { id: d.id } })}
                >
                  <span className="inline-flex items-center gap-1.5 font-display font-bold text-token-primary">
                    <ShieldCheck className="h-4 w-4 flex-none text-token-secondary" />
                    {d.doc_number}
                  </span>
                  <span className="truncate">{d.doc_type}</span>
                  <span className="truncate">{nameFor(d.contact_id)}</span>
                  <span className="tabular-nums">{fmtDate(d.issued_at)}</span>
                  <span className="text-right">
                    {d.signed_at && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-token-success">
                        <Lock className="h-3.5 w-3.5" /> Signed
                      </span>
                    )}
                  </span>
                </Trow>
              ))}
              <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
            </Table>
          )}
        </Card>
      </div>
      </>}

    </Page>
  )
}

/**
 * US7 — the safety-debt view. Two lists that are uncomfortable on purpose: high-risk jobs starting
 * in the next week with nothing signed, and the people who started high-risk work anyway. The
 * product does not escalate beyond making both visible; enforcement posture is the business's call.
 */
function SafetySection() {
  const debt = useSwmsDebt(7)
  const overrides = useSafetyOverrides(50)
  const { company } = useCompany()
  const { members } = useTeam()
  const j = jurisdictionFor(company?.state)
  const rows = debt.data ?? []
  const ovr = overrides.data ?? []
  const who = (uid: string | null) => {
    const m = members.find((x) => x.id === uid)
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Someone' : 'Someone'
  }

  return (
    <div className="mt-5 space-y-5">
      <Card>
        <div className="border-b border-token-subtle px-5 py-3.5">
          <p className="flex items-center gap-2 font-display text-[15px] font-bold text-token-primary">
            <HardHat className="h-4 w-4 text-token-warning" /> High-risk jobs with no signed SWMS — overdue, or starting within 7 days
          </p>
        </div>
        {debt.isLoading ? (
          <SkeletonRows count={3} />
        ) : rows.length === 0 ? (
          <EmptyState label="Nothing outstanding. Every high-risk job due now or this week has a signed SWMS." />
        ) : (
          <div className="divide-y divide-token-subtle">
            {rows.map((r) => (
              <a key={r.task_id} href={`/jobs/${r.task_id}`} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-token-surface">
                <span className="min-w-0">
                  <span className="block truncate text-[13.5px] font-medium text-token-primary">{r.name ?? `Job #${r.task_id}`}</span>
                  <span className="block truncate text-[12.5px] text-token-secondary">
                    {(r.categories ?? []).map((c) => hrcwLabel(c, j)).join(' · ')}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-[12.5px] text-token-secondary">
                    {r.planned_start_date ? fmtDate(r.planned_start_date) : 'Unscheduled'}
                  </span>
                  <span className="rounded-full bg-token-danger-surface px-2.5 py-1 text-[12px] font-medium text-token-danger">
                    {r.swms_status === 'draft' ? 'Started, unsigned' : 'No SWMS'}
                  </span>
                </span>
              </a>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="border-b border-token-subtle px-5 py-3.5">
          <p className="flex items-center gap-2 font-display text-[15px] font-bold text-token-primary">
            <AlertTriangle className="h-4 w-4 text-token-warning" /> Started without a signed SWMS
          </p>
        </div>
        {ovr.length === 0 ? (
          <EmptyState label="Nobody has overridden the safety gate." />
        ) : (
          <div className="divide-y divide-token-subtle">
            {ovr.map((o) => (
              <div key={o.id} className="px-5 py-3">
                <p className="text-[13.5px] text-token-primary">
                  {who(o.overridden_by)} started <a className="font-medium underline" href={`/jobs/${o.task_id}`}>job #{o.task_id}</a>
                  {' '}on {fmtDate(o.created_at)}
                </p>
                {o.reason && <p className="mt-0.5 text-[12.5px] text-token-secondary">“{o.reason}”</p>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
